import {
  MentorMetricsPeriod,
  getCurrentMentorMetricsMonth,
  parseMentorMetricsSort,
  mentorMetricsPeriodSchema,
} from "@/lib/api/mentor-metrics-dashboard";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { z } from "zod";

const PAGE_SIZES = [10, 20, 30, 50, 100] as const;

const leadMentorIdsSchema = z.preprocess(
  (value) =>
    typeof value === "string" ? value.split(",").filter(Boolean) : value,
  z.array(z.string().uuid()),
);

export const mentorMetricsSearchSchema = z.object({
  period: mentorMetricsPeriodSchema.catch(() => getCurrentMentorMetricsMonth()),
  leadMentorIds: leadMentorIdsSchema.catch([]),
  search: z.string().catch(""),
  sort: z.string().catch("tenure:asc"),
  page: z.coerce.number().int().positive().catch(1),
  pageSize: z.coerce
    .number()
    .refine((value) =>
      PAGE_SIZES.includes(value as (typeof PAGE_SIZES)[number]),
    )
    .catch(10),
});

export const useMentorMetricsParams = () => {
  const navigate = useNavigate();
  const unparsedSearch = useSearch({ strict: false });
  const params = mentorMetricsSearchSchema.parse(unparsedSearch);
  const sort = parseMentorMetricsSort(params.sort);

  const updateSearch = (
    updates: Partial<z.infer<typeof mentorMetricsSearchSchema>>,
  ) =>
    navigate({
      to: "/mentor-performance",
      search: {
        ...params,
        ...updates,
      },
      replace: true,
    });

  return {
    ...params,
    sort,
    pageSizes: PAGE_SIZES,
    setPeriod: (period: MentorMetricsPeriod) =>
      updateSearch({ period, page: 1 }),
    setLeadMentorIds: (leadMentorIds: string[]) =>
      updateSearch({ leadMentorIds, page: 1 }),
    setSearch: (search: string) => updateSearch({ search, page: 1 }),
    setSort: (nextSort: typeof sort) =>
      updateSearch({
        sort: nextSort
          .map(({ field, direction }) => `${field}:${direction}`)
          .join(","),
        page: 1,
      }),
    setPage: (page: number) => updateSearch({ page }),
    setPageSize: (pageSize: number) => updateSearch({ pageSize, page: 1 }),
  };
};
